import enquire from 'enquire.js';
import Waypoint from 'waypoints';
import {Breakpoints, utils} from '@shopify/marketing-assets';

const activeClass = 'js-demo-video--active';
const loadingClass = 'js-demo-video--loading';
const videoClass = 'demo-video__video';
const buttonClass = 'demo-video__button';
const playButtonClass = `${buttonClass}--play`;
const pauseButtonClass = `${buttonClass}--pause`;

function isVisible(elem) {
  return elem.offsetWidth > 0 || elem.offsetHeight > 0;
}

class DemoVideo {
  constructor(container) {
    this.isLoaded = false;
    this.container = container;
    this.video = container.querySelector(`.${videoClass}`);
    this.playButton = container.querySelector(`.${playButtonClass}`);
    this.pauseButton = container.querySelector(`.${pauseButtonClass}`);
    this.buttonWasClicked = false;

    this.bindFunctions();
    this.init();
  }

  bindFunctions() {
    this.playVideoClicked = this.playVideoClicked.bind(this);
    this.pauseVideoClicked = this.pauseVideoClicked.bind(this);
    this.onVideoClick = this.onVideoClick.bind(this);
    this.onVideoIsPlaying = this.onVideoIsPlaying.bind(this);
    this.onVideoIsPaused = this.onVideoIsPaused.bind(this);
    this.onDesktopEnter = this.onDesktopEnter.bind(this);
    this.onDesktopExit = this.onDesktopExit.bind(this);

    this.loadVideo = utils.once(this.loadVideo);
    this.onVideoLoadEnd = utils.once(this.onVideoLoadEnd.bind(this));
  }

  init() {
    // to indicate that the component is broken if JS is not working
    this.container.classList.remove(loadingClass);

    this.playButton.addEventListener('click', this.playVideoClicked);
    this.pauseButton.addEventListener('click', this.pauseVideoClicked);

    this.video.addEventListener('click', this.onVideoClick);
    this.video.addEventListener('play', this.onVideoIsPlaying);
    this.video.addEventListener('pause', this.onVideoIsPaused);

    this.bindScreenResize();
  }

  initLazyload() {
    this.lazyload = new Waypoint({
      element: this.video,
      offset: '100%',
      handler: (dir) => {
        if (dir !== 'down' || !isVisible(this.video)) {
          return;
        }
        this.loadVideo();
      },
    });
  }

  clearLazyload() {
    return this.lazyload && this.lazyload.disable();
  }

  onDesktopEnter() {
    this.initLazyload();
  }

  onDesktopExit() {
    this.clearLazyload();
  }

  bindScreenResize() {
    enquire.register(Breakpoints.prototype.phone, this.onDesktopExit);
    enquire.register(Breakpoints.prototype.tabletUp, this.onDesktopEnter);
  }

  onVideoIsPlaying() {
    this.onVideoLoadEnd();
    this.container.classList.add(activeClass);

    if (this.buttonWasClicked) {
      this.buttonWasClicked = false;
      this.pauseButton.focus();
    }
  }

  onVideoIsPaused() {
    this.container.classList.remove(activeClass);

    if (this.buttonWasClicked) {
      this.buttonWasClicked = false;
      this.playButton.focus();
    }
  }

  removeLazyloadListeners() {
    this.clearLazyload();
    enquire.unregister(Breakpoints.prototype.phone, this.onDesktopExit);
    enquire.unregister(Breakpoints.prototype.tabletUp, this.onDesktopEnter);
  }

  loadVideo() {
    this.removeLazyloadListeners();
    this.container.classList.add(loadingClass);
    this.isLoaded = true;

    this.video.querySelectorAll('source').forEach((source) => {
      source.src = source.getAttribute('data-src');
    });

    this.video.load();
  }

  onVideoClick() {
    if (this.video.paused) {
      this.playVideoClicked();
    } else {
      this.pauseVideoClicked();
    }
  }

  onVideoLoadEnd() {
    this.container.classList.remove(loadingClass);
  }

  playVideoClicked() {
    this.buttonWasClicked = true;
    this.playVideo();
  }

  pauseVideoClicked() {
    this.buttonWasClicked = true;
    this.pauseVideo();
  }

  playVideo() {
    if (this.isLoaded) {
      this.video.play()
        .then(() => {}) // eslint-disable-line no-empty-function
        .catch(() => {}); // eslint-disable-line no-empty-function
    } else {
      this.loadVideo();
    }
  }

  pauseVideo() {
    this.video.pause();
  }
}

export default DemoVideo;

